import { useEventBus } from '@vueuse/core'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { Notification, NotificationFirestoreData } from '~/shared/types/notification.js'
import { toMillis } from '~/shared/utils/dates.js'
import { normalizeNotification } from '~/shared/utils/notifications.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, holdLive, isLiveRunning, liveQueryListener, stopLive } from '~/utils/liveData'

/**
 * Store for managing notifications
 *
 * Follows the pattern of other stores in the application with:
 * - State management for notifications
 * - CRUD operations for Firestore
 * - Real-time subscription handling
 * - Helper methods for notification management
 */
// Track active subscriptions outside reactive state — functions shouldn't be reactive
// The member whose notifications subscribeToNotifications holds, and its release
let held: { key: string, release: () => void } | null = null

const NOTIFICATIONS_SHOWN = 100

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    // Store notifications by document ID for O(1) lookups
    notifications: {} as Record<string, Notification>,
    loading: false,
    error: null as string | null,
    initialized: false,
    // Track if we're in the initial load phase
    initialLoadComplete: false,
  }),

  getters: {
    /**
     * Convert map to array for components that expect arrays
     * Sorted by creation date (newest first)
     */
    notificationsArray(): Notification[] {
      return Object.values(this.notifications).sort((a, b) => {
        // Safely handle date comparison
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0
        return bTime - aTime
      })
    },

    /**
     * Get unread notifications
     */
    unreadNotifications(): Notification[] {
      return this.notificationsArray.filter(n => !n.read)
    },

    /**
     * Unread count for the badge, counted from the notifications. It was a
     * counter that each write and the live listener both bumped, so one new
     * notification could count twice
     */
    unreadCount(): number {
      return Object.values(this.notifications).filter(n => !n.read).length
    },

    /**
     * Get notifications by category
     */
    getNotificationsByCategory: state => (category: string): Notification[] => {
      return Object.values(state.notifications)
        .filter(n => n.category === category)
        .sort((a, b) => {
          // Safely handle date comparison
          const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0
          const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0
          return bTime - aTime
        })
    },

    /**
     * Get notification by ID
     */
    getNotificationById: state => (id: string): Notification | undefined => {
      return state.notifications[id]
    },
  },

  actions: {
    /**
     * Convert Firestore document data to a Notification object
     */
    convertToNotification(docId: string, data: NotificationFirestoreData): Notification {
      return normalizeNotification({
        id: docId,
        title: data.title,
        body: data.body,
        createdAt: data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : new Date(data.createdAt as Date),
        read: data.read || false,
        readAt: data.readAt instanceof Timestamp
          ? data.readAt.toDate()
          : data.readAt as Date | null,
        linkType: data.linkType,
        linkTarget: data.linkTarget,
        emailSent: data.emailSent || false,
        emailScheduled: data.emailScheduled || false,
        emailSentAt: data.emailSentAt instanceof Timestamp
          ? data.emailSentAt.toDate()
          : data.emailSentAt as Date | undefined,
        category: data.category,
        priority: data.priority || 'normal',
        metadata: data.metadata || {},
      })
    },

    /**
     * Convert a Notification object to Firestore-compatible data
     */
    convertToFirestoreData(notification: Notification): NotificationFirestoreData {
      const firestoreData: NotificationFirestoreData = {
        title: notification.title,
        body: notification.body,
        createdAt: notification.createdAt,
        read: notification.read,
        readAt: notification.readAt,
        linkType: notification.linkType,
        linkTarget: notification.linkTarget,
        emailSent: notification.emailSent,
        emailScheduled: notification.emailScheduled,
        emailSentAt: notification.emailSentAt,
        category: notification.category,
        priority: notification.priority,
        metadata: notification.metadata || {},
      }

      // Clean object recursively to remove undefined values (including in nested objects)
      return this.cleanObjectForFirestore(firestoreData)
    },

    // Helper method to recursively clean objects for Firestore
    cleanObjectForFirestore(obj: NotificationFirestoreData): NotificationFirestoreData {
      // A Record view, because `Object.keys` yields `string` and so cannot
      // index the typed shape.
      const result = { ...obj } as Record<string, unknown>

      Object.keys(result).forEach((key) => {
        const value = result[key]
        if (value === undefined) {
          delete result[key]
        }
        // Dates are preserved as-is; anything else object-shaped recurses.
        else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
          result[key] = this.cleanObjectForFirestore(value as NotificationFirestoreData)
        }
      })

      return result as unknown as NotificationFirestoreData
    },

    /**
     * Create a new notification for a member
     * @param memberId ID of the member to create notification for
     * @param notification Notification data to create
     * @returns ID of the created notification
     */
    async createNotification(memberId: string, notification: Partial<Notification>): Promise<string> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationsRef = collection(db, `members/${memberId}/notifications`)
        const newNotificationRef = doc(notificationsRef)

        const newNotification: Notification = normalizeNotification({
          id: newNotificationRef.id,
          title: notification.title || '',
          body: notification.body || '',
          createdAt: new Date(),
          read: false,
          linkType: notification.linkType || 'none',
          linkTarget: notification.linkTarget,
          emailSent: false,
          emailScheduled: notification.emailScheduled || false,
          emailSentAt: undefined,
          category: notification.category || 'system',
          priority: notification.priority || 'normal',
          metadata: notification.metadata || {},
        })

        const notificationData = this.convertToFirestoreData(newNotification)
        // Use server timestamp for createdAt
        notificationData.createdAt = serverTimestamp()

        console.warn(`[Notifications] Creating new notification with ID ${newNotification.id} for member ${memberId}`)
        await setDoc(newNotificationRef, notificationData)

        // Add to local state if we are subscribed to this member's notifications
        const currentUser = useCurrentUser().value
        if (this.initialized && currentUser && memberId === currentUser.uid) {
          console.warn(`[Notifications] Updating local state for new notification ${newNotification.id}`)
          this.$patch((state) => {
            state.notifications[newNotification.id] = newNotification
          })
        }

        // If emailScheduled is true, queue an email using the email service
        if (newNotification.emailScheduled) {
          try {
            // Get member data to get the email address
            const membersStore = useMembersStore()
            const member = await membersStore.fetchMember(memberId)

            if (member && member.email) {
              console.warn(`[Notifications] Queueing email for notification ${newNotification.id} to ${member.email}`)

              // Use the email service to queue the email securely
              const emailStore = useEmailStore()
              const emailId = await emailStore.queueNotificationEmail(
                newNotification,
                member.email,
                member.name || 'Member',
                memberId,
              )

              // Update notification with email sent status if email was queued successfully
              if (emailId) {
                // Update local state if needed
                if (this.initialized && currentUser && memberId === currentUser.uid) {
                  this.$patch((state) => {
                    const notification = state.notifications[newNotification.id]
                    if (notification) {
                      notification.emailSent = true
                      notification.emailSentAt = new Date()
                    }
                  })
                }

                console.warn(`[Notifications] Email queued successfully with ID ${emailId}`)
              }
            }
            else {
              console.warn(`[Notifications] Cannot send email for notification ${newNotification.id}: Member ${memberId} has no email address`)
            }
          }
          catch (emailError) {
            console.error(`[Notifications] Error sending email for notification ${newNotification.id}:`, emailError)
            // We don't throw the error here - we want to return the notification ID even if email sending fails
          }
        }

        return newNotification.id
      }
      catch (error) {
        console.error('Error creating notification:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Mark a notification as read
     * @param notificationId ID of the notification to mark as read
     * @param memberId ID of the member the notification belongs to
     */
    async markAsRead(notificationId: string, memberId: string): Promise<boolean> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationRef = doc(db, `members/${memberId}/notifications/${notificationId}`)

        await updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp(),
        })

        // Update local state
        if (this.notifications[notificationId] && !this.notifications[notificationId].read) {
          this.$patch((state) => {
            if (state.notifications[notificationId]) {
              state.notifications[notificationId].read = true
              state.notifications[notificationId].readAt = new Date()
            }
          })
        }

        return true
      }
      catch (error) {
        console.error('Error marking notification as read:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Mark all notifications as read for a member
     * @param memberId ID of the member
     */
    async markAllAsRead(memberId: string): Promise<boolean> {
      try {
        this.loading = true

        const db = useFirestore()

        const notificationsRef = collection(db, `members/${memberId}/notifications`)

        // Get all unread notifications
        const q = query(notificationsRef, where('read', '==', false))
        const querySnapshot = await getDocs(q)

        if (querySnapshot.empty) {
          return true // No unread notifications to update
        }
        const batch = writeBatch(db)
        // Update each notification in a batch
        querySnapshot.forEach((docSnapshot) => {
          const notificationRef = doc(db, `members/${memberId}/notifications/${docSnapshot.id}`)
          batch.update(notificationRef, {
            read: true,
            readAt: serverTimestamp(),
          })
        })

        await batch.commit()

        // Update local state
        this.$patch((state) => {
          Object.keys(state.notifications).forEach((id) => {
            if (state.notifications[id] && !state.notifications[id].read) {
              state.notifications[id].read = true
              state.notifications[id].readAt = new Date()
            }
          })
        })

        return true
      }
      catch (error) {
        console.error('Error marking all notifications as read:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Delete a notification
     * @param notificationId ID of the notification to delete
     * @param memberId ID of the member the notification belongs to
     */
    async deleteNotification(notificationId: string, memberId: string): Promise<boolean> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationRef = doc(db, `members/${memberId}/notifications/${notificationId}`)

        await deleteDoc(notificationRef)

        // Update local state
        this.$patch((state) => {
          delete state.notifications[notificationId]
        })

        return true
      }
      catch (error) {
        console.error('Error deleting notification:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * A member's latest notifications, live while the caller holds them
     * (utils/liveData.ts). Throws when they can't be read.
     */
    async fetchNotifications(memberId: string, notificationLimit = NOTIFICATIONS_SHOWN): Promise<Notification[]> {
      this.loading = !this.initialized
      try {
        await acquireLive(this.notificationsSpec(memberId, notificationLimit))
      }
      catch (error) {
        console.error('Error fetching notifications:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        throw error
      }
      finally {
        this.loading = false
      }
      return Object.values(this.notifications)
        .sort((a, b) => (toMillis(b.createdAt) ?? 0) - (toMillis(a.createdAt) ?? 0))
    },

    /**
     * Hold a member's latest notifications live: the bell and the
     * new-notification toasts. Returns the release.
     */
    subscribeToNotifications(memberId: string): () => void {
      const spec = this.notificationsSpec(memberId, NOTIFICATIONS_SHOWN)
      // Held already, unless the listener has since stopped (sign-out)
      if (held?.key !== spec.key || !isLiveRunning(spec.key)) {
        held?.release()
        const { ready, release } = holdLive(spec)
        held = { key: spec.key, release }
        // New ones from here on are announced; the first snapshot isn't
        ready.then(() => {
          this.$patch((state) => {
            state.initialLoadComplete = true
          })
        }, (error: unknown) => {
          this.error = error instanceof Error ? error.message : String(error)
          this.$patch((state) => {
            state.initialLoadComplete = true
          })
        })
      }
      const key = spec.key
      return () => {
        if (held?.key !== key) return
        held.release()
        held = null
      }
    },

    notificationsSpec(memberId: string, notificationLimit: number): LiveSpec<NotificationFirestoreData> {
      const collectionPath = `members/${memberId}/notifications`
      return {
        key: `${collectionPath}?latest=${notificationLimit}`,
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), collectionPath), orderBy('createdAt', 'desc'), limit(notificationLimit)),
          ...liveQueryListener(collectionPath, emit, fail),
        ),
        apply: changes => this.applyNotifications(changes),
        evict: paths => this.evictNotifications(paths),
        ready: () => {
          this.$patch((state) => {
            state.initialized = true
          })
        },
      }
    },

    applyNotifications(changes: LiveChange<NotificationFirestoreData>[]) {
      const announce: Notification[] = []
      this.$patch((state) => {
        for (const { path, data } of changes) {
          const notification = this.convertToNotification(path.slice(path.lastIndexOf('/') + 1), data!)
          if (this.initialLoadComplete && !state.notifications[notification.id] && !notification.read) {
            announce.push(notification)
          }
          state.notifications[notification.id] = notification
        }
      })
      if (announce.length > 0) {
        const eventBus = useEventBus('new-notification')
        for (const notification of announce) eventBus.emit(notification)
      }
    },

    evictNotifications(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.notifications[path.slice(path.lastIndexOf('/') + 1)]
      })
    },

    /**
     * Stop the notifications listener and empty the store (sign-out, or
     * before setting them up for another member)
     */
    cleanupSubscriptions() {
      if (held) {
        held.release()
        stopLive(held.key)
        held = null
      }
      this.$patch((state) => {
        state.notifications = {}
        state.loading = false
        state.error = null
        state.initialized = false
        state.initialLoadComplete = false
      })
    },

    /**
     * Reset the store state completely
     */
    $reset() {
      // Clean up subscriptions, which also resets the state
      this.cleanupSubscriptions()
    },
  },
})
